import React, { useEffect, useRef } from 'react'
import { Common } from '@trimblemaps/trimblemaps-js';
import { LOAD_MAP_PADDING } from '../../../utils/constants'
import { GeographicalService } from '../../../utils/common'
import './map.scss'
import "../../../components/shared/PCMilerMap/map.scss";
import { getLongitudeLatitude, renderMapStopMarkerAndPopup } from './utils'
import { PCMilerLoadMapProps, StopDetails } from './PCMilerLoadMap.type';
import { DirectionLocation, DirectionsWaypoint } from '../GoogleMap/map/map.type';
import { LngLat } from './PCMiler.type';

export const PCMilerLoadMap = (props: PCMilerLoadMapProps) => {
    const {
        origin,
        destination,
        waypoints,
        onLoaded,
        fullscreenControl = true
    } = props;

    const MAP_CONTAINER_DOM = useRef<HTMLDivElement>(null);
    const map = useRef<TrimbleMaps.Map>();
    const bounds = useRef<TrimbleMaps.LngLatBounds>();
    const fitBoundsOptions = useRef<TrimbleMaps.FitBoundsOptions>();
    const markers = useRef<TrimbleMaps.Marker[]>([]);
    const route = useRef<TrimbleMaps.Route>();
    const cachedLatlngs = useRef<any>({});

    useEffect(() => {
        try {
            if (map.current && markers.current && route.current) {
                markers.current.forEach(marker => marker.remove());
                route.current.remove();
            }
            renderLoadMap();
        } catch (error) {
            console.error(error);
        }
    }, [JSON.stringify(waypoints), JSON.stringify(origin), JSON.stringify(destination)]);

    async function renderLoadMap() {
        const mapContainer = MAP_CONTAINER_DOM.current!;
        let myMapRoute: TrimbleMaps.Route | undefined;
        let originStop: StopDetails = await getStopDetails(origin);
        let destinationStop: StopDetails = await getStopDetails(destination);
        let waypointsStops: StopDetails[] | never[] = waypoints?.length ? await Promise.all(waypoints.map(async (waypoint: DirectionsWaypoint) => await getStopDetails(waypoint.location))) : [];
        let stops: StopDetails[] = [originStop, ...waypointsStops, destinationStop];
        const { latLng: { lng, lat } = { lng: 0, lat: 0 }, shipmentId } = originStop || {};
        const myMapBounds: TrimbleMaps.LngLatBounds = GeographicalService.details.createBounds({ lat, lng }, { lat, lng });
        stops.forEach(({ latLng }) => myMapBounds.extend(latLng));
        let routeStops: LngLat[] = stops.map(({ latLng }) => latLng);
        const myMapFitBoundsOptions: TrimbleMaps.FitBoundsOptions = { padding: LOAD_MAP_PADDING };
        const style: string = Common.Style.DEFAULT;
        if (shipmentId) {
            myMapRoute = GeographicalService.details.renderRoute(shipmentId, routeStops, myMapFitBoundsOptions);
        }
        if (map.current) {
            if (myMapRoute) {
                myMapRoute.addTo(map.current);
                renderLoadMapMarkersAndPopup(stops, map.current);
            }
            route.current = myMapRoute;
            bounds.current = myMapBounds;
            fitBoundsOptions.current = myMapFitBoundsOptions;
            mapFitBoundsResize();
        } else {
            const isLimitedZoom = !(props?.origin?.addressLine1 && props?.destination?.addressLine1)
            const myMap: TrimbleMaps.Map = GeographicalService.details.renderMap(isLimitedZoom, mapContainer, myMapBounds, style, myMapFitBoundsOptions);
            const myMapNavControl: TrimbleMaps.NavigationControl = GeographicalService.details.navControl({ showCompass: false });
            myMap.addControl(myMapNavControl, "bottom-right");
            if (fullscreenControl) {
                const myMapFullScreenControl: TrimbleMaps.FullscreenControl = GeographicalService.details.renderFullscreenControl();
                myMap.addControl(myMapFullScreenControl, 'top-right')
                const trimblemapsFullscreenButton: any = document.querySelector(".trimblemaps-ctrl-icon.trimblemaps-ctrl-fullscreen")!;
                if (trimblemapsFullscreenButton) {
                    trimblemapsFullscreenButton.onclick = () => {
                        setTimeout(() => {
                            myMap.resize();
                        }, 100);
                    }
                }
            }
            myMap.on('load', function () {
                if(myMap.getLayer("labels_facility")) myMap.removeLayer("labels_facility");
                onLoaded && onLoaded();
                if (myMapRoute) {
                    myMapRoute.addTo(myMap);
                    renderLoadMapMarkersAndPopup(stops, myMap);
                }
                map.current = myMap;
                route.current = myMapRoute;
                bounds.current = myMapBounds;
                fitBoundsOptions.current = myMapFitBoundsOptions;
                mapFitBoundsResize();
            });
        }
    }

    function renderLoadMapMarkersAndPopup(stops: StopDetails[], mapComponent: TrimbleMaps.Map) {
        stops?.forEach((stop: StopDetails, stopIndex: number, ) => {
            const { latLng, stopType, city, state } = stop;
            const popupTitle: string = `${city}, ${state}`
            const marker: TrimbleMaps.Marker = renderMapStopMarkerAndPopup(latLng, stopIndex, stops.length, stopType, popupTitle, mapComponent)
            markers.current.push(marker);
        });
    }

    async function getStopDetails(stop: DirectionLocation): Promise<StopDetails> {
        let { city = "", state = "", stopId, locationType, shipmentId } = stop;
        let latLng;
        if (cachedLatlngs.current[stopId]) {
            latLng = cachedLatlngs.current[stopId];
        } else {
            latLng = await getLongitudeLatitude(stop)
            cachedLatlngs.current[stopId] = latLng;
        }
        return {
            city,
            state,
            latLng,
            stopType: locationType,
            shipmentId
        }
    }

    function mapFitBoundsResize() {
        map?.current?.resize();
        map.current && bounds.current && map.current.fitBounds(bounds.current, fitBoundsOptions.current);
    }

    return <div ref={MAP_CONTAINER_DOM} className="pc-miler-map"></div>
}